/**
 * Rhythmz — SceneManager
 * ───────────────────────
 * Orchestrates the scene lifecycle: holds an array of registered scenes,
 * auto-cycles on a timer, crossfades between scenes with actual opacity
 * blending, and dispatches events so the host (VisualizerEngine) can update
 * presets / UI.
 *
 * Crossfade: during transition, the old scene's opacity fades 1→0 while the
 * new scene's opacity fades 0→1. Both scenes coexist in the scene graph.
 * Opacity is set by traversing the group and adjusting material.transparent +
 * material.opacity on every mesh/material found.
 */

import * as THREE from 'three/webgpu';

export interface SceneHandle {
  group: THREE.Group;
  update: (dt: number) => void;
  dispose: () => void;
  name?: string;
}

export type SceneFactory = () => SceneHandle;

export interface SceneManagerConfig {
  autoCycleInterval?: number;
  transitionSeconds?: number;
}

export class SceneManager {
  private scenes: SceneFactory[] = [];
  private activeHandle: SceneHandle | null = null;
  private nextHandle: SceneHandle | null = null;
  private currentIdx = -1;
  private nextIdx = -1;

  private autoCycle = true;
  private cycleInterval = 30000;
  private transitionTime = 1.5;
  private lastCycleTime = 0;

  private transitioning = false;
  private transitionProgress = 0;

  private parent: THREE.Scene | null = null;

  onSceneChange: ((name: string) => void) | null = null;

  constructor(config?: SceneManagerConfig) {
    if (config?.autoCycleInterval) this.cycleInterval = config.autoCycleInterval;
    if (config?.transitionSeconds) this.transitionTime = config.transitionSeconds;
  }

  register(...factories: SceneFactory[]): void {
    this.scenes.push(...factories);
  }

  attach(parent: THREE.Scene): void {
    this.parent = parent;
  }

  start(): void {
    this.autoCycle = true;
    this.loadScene(0);
    this.lastCycleTime = performance.now();
  }

  stop(): void {
    this.autoCycle = false;
  }

  next(): void {
    if (this.scenes.length === 0) return;
    const ni = (this.currentIdx + 1) % this.scenes.length;
    this.loadScene(ni);
  }

  prev(): void {
    if (this.scenes.length === 0) return;
    const pi = (this.currentIdx - 1 + this.scenes.length) % this.scenes.length;
    this.loadScene(pi);
  }

  loadScene(idx: number): void {
    if (idx < 0 || idx >= this.scenes.length) return;
    if (idx === this.currentIdx && this.activeHandle && !this.transitioning) return;

    const factory = this.scenes[idx];
    const newHandle = factory();

    if (this.activeHandle && this.parent) {
      // Start crossfade transition
      this.parent.add(newHandle.group);
      this.setGroupOpacity(newHandle.group, 0); // start invisible
      this.nextHandle = newHandle;
      this.nextIdx = idx;
      this.transitioning = true;
      this.transitionProgress = 0;
    } else {
      this.activeHandle?.dispose();
      this.activeHandle = newHandle;
      this.currentIdx = idx;
      if (this.parent) this.parent.add(newHandle.group);
      this.onSceneChange?.(newHandle.name ?? `Scene ${idx}`);
    }

    this.lastCycleTime = performance.now();
  }

  /** Set opacity on all materials in a group (recursively). */
  private setGroupOpacity(group: THREE.Object3D, opacity: number): void {
    group.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.material) {
        const mat = mesh.material as THREE.Material & { opacity?: number; transparent?: boolean };
        mat.transparent = true;
        if (mat.opacity !== undefined) mat.opacity = opacity;
      }
      const points = obj as THREE.Points;
      if (points.material) {
        const pmat = points.material as THREE.Material & { opacity?: number; transparent?: boolean };
        pmat.transparent = true;
        if (pmat.opacity !== undefined) pmat.opacity = opacity;
      }
      const lines = obj as THREE.LineSegments;
      if (lines.material) {
        const lmat = lines.material as THREE.Material & { opacity?: number; transparent?: boolean };
        lmat.transparent = true;
        if (lmat.opacity !== undefined) lmat.opacity = opacity;
      }
    });
  }

  update(dt: number): void {
    if (this.activeHandle) {
      this.activeHandle.update(dt);
    }

    if (this.transitioning && this.nextHandle) {
      this.nextHandle.update(dt);
      this.transitionProgress += dt / this.transitionTime;

      // Crossfade opacity
      const fadeIn = Math.min(1, this.transitionProgress);
      const fadeOut = Math.max(0, 1 - this.transitionProgress);
      if (this.activeHandle) this.setGroupOpacity(this.activeHandle.group, fadeOut);
      this.setGroupOpacity(this.nextHandle.group, fadeIn);

      if (this.transitionProgress >= 1) {
        this.transitioning = false;
        if (this.activeHandle && this.parent) {
          this.parent.remove(this.activeHandle.group);
          this.activeHandle.dispose();
        }
        this.activeHandle = this.nextHandle;
        this.currentIdx = this.nextIdx;
        this.nextHandle = null;
        this.nextIdx = -1;
        // Restore full opacity on the new active scene
        this.setGroupOpacity(this.activeHandle.group, 1);
        this.onSceneChange?.(this.activeHandle.name ?? `Scene ${this.currentIdx}`);
      }
    }

    if (this.autoCycle && !this.transitioning) {
      const now = performance.now();
      if (now - this.lastCycleTime > this.cycleInterval) {
        this.next();
      }
    }
  }

  get currentSceneName(): string {
    return this.activeHandle?.name ?? 'None';
  }

  dispose(): void {
    this.activeHandle?.dispose();
    this.activeHandle = null;
    this.nextHandle?.dispose();
    this.nextHandle = null;
    this.scenes = [];
    this.parent = null;
  }
}
